// Orquestra a transição "Iniciar etapa": tint da linha → saída da lista do
// módulo → abre o guia + loader nomeado + navega. Extraída como função pura
// (recebe store/router por parâmetro, não depende de instância de componente)
// pra ficar reutilizável e testável isolada — antes vivia dentro do modal de
// preview (central-config-step-preview.vue), removido em 2026-07-25 a pedido
// do usuário ("por que ainda aparece o modal?" — decisão explícita de tirar o
// passo intermediário; clicar na linha agora vai direto pra transição).
import { wait, prefersReducedMotion } from './motion'
import { GUIDE_ORIGIN_JOURNEY } from './guide-origin'
import { montarTourDaEtapa } from './tour-registry'

const PRE_EXIT_DELAY_MS = 700
const EXIT_TRANSITION_MS = 300
const EXIT_TRANSITION_REDUCED_MS = 120

// Sem task.url (task action !== 'redirect') ou push que não navega (destino =
// rota atual) — nos dois casos o router.afterEach global NUNCA dispara, então
// finishStepNavigation() (router/index.js) não roda. ⚠️ Neste app o push NÃO
// rejeita nesse caso: router/index.js sobrescreve VueRouter.prototype.push com
// `.catch(swallowNavigationFailure)`, que engole o NavigationFailure (inclusive
// NavigationDuplicated) e RESOLVE com undefined — um `.catch` aqui nunca roda.
// Sem este reset direto, a linha ficava presa em "Abrindo …" até a PRÓXIMA
// navegação qualquer no app, e como o guard `if (navigatingTaskId) return` do
// accordion é global, isso travava "Iniciar etapa" no app inteiro (review
// 2026-07-26; medido de novo em 2026-09-11 nas etapas do Compliance que apontam
// para /compliance/visao-geral, a mesma tela onde vive a barra da Jornada).
function resetStuckTransition(store) {
  store.dispatch('centralConfig/resetStepTransition')
}

// `skipChoreography` pula APENAS a animação: os dois `wait` (700ms + 300ms) e o
// `exitingModuleId`, que dispara o `.cc-timeline--exiting` da lista do módulo.
// Existe porque esses efeitos animam elementos que só existem na tela da Jornada;
// chamada de outra tela (o CTA das telas de boas-vindas é o caso real), a
// coreografia vira ~1s de tela inerte animando o que não está montado.
//
// ⚠️ O `navigatingTaskId` continua sendo despachado nos DOIS caminhos, e não é
// detalhe de animação — ele carrega duas responsabilidades:
//   • guard global de clique duplo enquanto a navegação está em voo;
//   • sinal que `finishStepNavigation` (router/index.js) lê no `afterEach` para
//     emitir `CENTRAL_CONFIG_FOCUS_GUIDE` e mover o foco para o drawer.
// Pulá-lo aqui deixaria o usuário de teclado/leitor de tela chegar numa tela
// nova com um painel de 380px aberto e o foco em lugar nenhum.
export async function startTaskNavigation({
  store,
  router,
  moduleId,
  task,
  skipChoreography = false,
  // De onde a etapa foi iniciada — repassado ao guia para o botão de voltar
  // saber o destino (ver utils/guide-origin.js). Omitir mantém o comportamento
  // de sempre (voltar para a home da Jornada), que é o caso dos dois chamadores
  // existentes: o accordion e o CTA das telas de boas-vindas.
  origin = GUIDE_ORIGIN_JOURNEY,
  moduleKey = null,
}) {
  if (!task) return

  store.dispatch('centralConfig/setStepTransition', {
    navigatingTaskId: task.id,
  })

  if (!skipChoreography) {
    await wait(PRE_EXIT_DELAY_MS)

    store.dispatch('centralConfig/setStepTransition', {
      navigatingTaskId: task.id,
      exitingModuleId: moduleId,
    })

    await wait(
      prefersReducedMotion() ? EXIT_TRANSITION_REDUCED_MS : EXIT_TRANSITION_MS
    )
  }

  store.dispatch('centralConfig/openGuide', {
    moduleId,
    taskId: task.id,
    origin,
    moduleKey,
  })

  // ★ TOUR GUIADO (2026-08-14). Cada "Iniciar etapa" passa por aqui — a barra
  // embutida na Visão Geral do módulo, o accordion da Jornada, o card da Home e
  // o CTA das telas de boas-vindas. Iniciar o tour NESTE ponto é o que faz as
  // quatro superfícies guiarem sem cada uma reimplementar o mesmo passo.
  // Módulo sem provedor registrado segue com o drawer de texto, como sempre.
  iniciarTourSePossivel({ store, moduleKey, moduleId, task })

  if (!task.url) {
    resetStuckTransition(store)
    return
  }
  // Reset nos DOIS desfechos do push. Numa navegação real o afterEach já
  // resetou (e emitiu o foco do drawer) antes deste `.then` — o reset extra é
  // idempotente. Sem navegação o foco NÃO é emitido de propósito: a tela não
  // mudou, então o foco continua na linha clicada, não perdido numa tela nova.
  const reset = () => resetStuckTransition(store)
  router.push(task.url).then(reset, reset)
}

// Extraído do corpo acima só pelo limite de statements do ESLint.
//
// ⚠️ O `collapseGuide` vem DEPOIS do `openGuide` de propósito: aquele grava
// `collapsed: false` por contrato ("abrir uma tarefa sempre expande"), então
// recolher antes é sobrescrito em silêncio. E não é estética — com o painel de
// 380px aberto, cada navegação entre etapas liga/desliga `padding-right` na
// raiz e cada getBoundingClientRect muda sob os pés do furo de luz.
function iniciarTourSePossivel({ store, moduleKey, moduleId, task }) {
  const tour = montarTourDaEtapa({ store, moduleKey, moduleId, task })
  if (!tour) return

  const guiaExpandido = !!store.getters['centralConfig/guidePanelOpen']
  store.dispatch('centralConfig/iniciarTour', {
    ...tour,
    recolheuGuia: guiaExpandido,
  })
  store.dispatch('centralConfig/collapseGuide')
}
